import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  X, ArrowRight, ArrowLeft, Check, QrCode, MapPin, Calendar as CalIcon,
  Receipt, FileText, Sparkles, TrendingUp, AlertTriangle, Clock,
} from "lucide-react";
import { Button } from "./ui/button";

/* =========================================================
   VERIXA · Interactive Tour Modal — "Try Verixa in 60s"
   4 animated mock steps: Timbratura → Ferie → Rimborso → AI Report
   ========================================================= */

const STEP_COUNT = 4;

function ProgressDots({ active }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: STEP_COUNT }).map((_, i) => (
        <span
          key={`step-dot-${i}`}
          className="h-1.5 rounded-full transition-all duration-500"
          style={{
            width: i === active ? 32 : 8,
            background: i <= active
              ? "linear-gradient(90deg, #3B82F6 0%, #7C3AED 100%)"
              : "rgba(15,23,42,0.10)",
          }}
        />
      ))}
    </div>
  );
}

/* ---------- Step 1: QR Check-in ---------- */
function QRStep({ done, onAction }) {
  return (
    <div className="relative rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 100%)", height: 320 }}>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="relative mx-auto mb-4 w-40 h-40 rounded-2xl bg-white p-3 shadow-2xl">
            {/* Fake QR */}
            <div className="w-full h-full" style={{
              backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='white'/><g fill='black'><rect x='8' y='8' width='24' height='24'/><rect x='14' y='14' width='12' height='12' fill='white'/><rect x='17' y='17' width='6' height='6' fill='black'/><rect x='68' y='8' width='24' height='24'/><rect x='74' y='14' width='12' height='12' fill='white'/><rect x='77' y='17' width='6' height='6' fill='black'/><rect x='8' y='68' width='24' height='24'/><rect x='14' y='74' width='12' height='12' fill='white'/><rect x='17' y='77' width='6' height='6' fill='black'/><rect x='40' y='10' width='6' height='6'/><rect x='50' y='10' width='6' height='6'/><rect x='40' y='20' width='6' height='6'/><rect x='10' y='40' width='6' height='6'/><rect x='20' y='40' width='6' height='6'/><rect x='40' y='40' width='6' height='6'/><rect x='50' y='40' width='6' height='6'/><rect x='60' y='40' width='6' height='6'/><rect x='40' y='50' width='6' height='6'/><rect x='60' y='50' width='6' height='6'/><rect x='40' y='60' width='6' height='6'/><rect x='50' y='60' width='6' height='6'/><rect x='60' y='60' width='6' height='6'/><rect x='70' y='40' width='6' height='6'/><rect x='80' y='50' width='6' height='6'/><rect x='40' y='70' width='6' height='6'/><rect x='50' y='80' width='6' height='6'/><rect x='70' y='70' width='6' height='6'/><rect x='80' y='80' width='6' height='6'/></g></svg>\")",
              backgroundSize: "cover"
            }} />
            {!done && (
              <div className="absolute inset-0 overflow-hidden rounded-xl">
                <div className="qr-scan-line" />
              </div>
            )}
            {done && (
              <div className="absolute inset-0 rounded-xl flex items-center justify-center" style={{ background: "rgba(16,185,129,0.95)" }}>
                <Check className="w-16 h-16 text-white" strokeWidth={3} />
              </div>
            )}
          </div>
          <div className="text-white text-[14px] font-semibold mb-1">Sede Milano · Via Dante 12</div>
          <div className="inline-flex items-center gap-1.5 text-[11.5px] font-mono" style={{ color: "rgba(255,255,255,0.65)" }}>
            <MapPin className="w-3 h-3" /> GPS validato · 45.466° N, 9.190° E
          </div>
        </div>
      </div>
      {done && (
        <div className="absolute bottom-4 left-4 right-4 rounded-xl px-4 py-3 backdrop-blur reveal" style={{ background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.40)" }}>
          <div className="flex items-center gap-2 text-white text-[13px] font-semibold">
            <Check className="w-4 h-4 text-emerald-400" />
            Ingresso registrato · 09:02:14
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Step 2: Vacation request ---------- */
function VacationStep({ done, onAction }) {
  return (
    <div className="rounded-2xl p-5 md:p-6" style={{ background: "#F8FAFC", border: "1px solid rgba(15,23,42,0.06)", minHeight: 320 }}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)" }}>
          <CalIcon className="w-4.5 h-4.5" />
        </div>
        <div>
          <div className="text-[14.5px] font-bold" style={{ color: "#0F172A" }}>Nuova richiesta ferie</div>
          <div className="text-[11.5px]" style={{ color: "#64748B" }}>Saldo disponibile: 18 giorni</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-lg p-3 bg-white border border-[rgba(15,23,42,0.08)]">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.18em]" style={{ color: "#94A3B8" }}>Dal</div>
          <div className="text-[15px] font-bold mt-1" style={{ color: "#0F172A" }}>Lun 12 Ago</div>
        </div>
        <div className="rounded-lg p-3 bg-white border border-[rgba(15,23,42,0.08)]">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.18em]" style={{ color: "#94A3B8" }}>Al</div>
          <div className="text-[15px] font-bold mt-1" style={{ color: "#0F172A" }}>Ven 23 Ago</div>
        </div>
      </div>

      <div className="rounded-lg p-3 bg-white border border-[rgba(15,23,42,0.08)] mb-3">
        <div className="text-[10.5px] font-mono uppercase tracking-[0.18em]" style={{ color: "#94A3B8" }}>Motivazione</div>
        <div className="text-[13.5px] mt-1" style={{ color: "#0F172A" }}>Vacanza estiva in famiglia</div>
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)" }}>
        <div className="text-[12.5px]" style={{ color: "#1E40AF" }}>
          <span className="font-semibold">10 giorni lavorativi</span> · 4 in agosto
        </div>
        <Sparkles className="w-4 h-4" style={{ color: "#3B82F6" }} />
      </div>

      {done && (
        <div className="mt-4 rounded-xl px-4 py-3 reveal" style={{ background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.30)" }}>
          <div className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: "#047857" }}>
            <Check className="w-4 h-4" />
            Inviato a Marco R. (HR Director). Approvazione media: 4h.
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Step 3: Expense approval ---------- */
function ExpenseStep({ done, onAction }) {
  return (
    <div className="rounded-2xl p-5 md:p-6" style={{ background: "#F8FAFC", border: "1px solid rgba(15,23,42,0.06)", minHeight: 320 }}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)" }}>
          <Receipt className="w-4.5 h-4.5" />
        </div>
        <div>
          <div className="text-[14.5px] font-bold" style={{ color: "#0F172A" }}>Rimborso da approvare</div>
          <div className="text-[11.5px]" style={{ color: "#64748B" }}>Da: Giulia P. · 2 minuti fa</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="rounded-lg p-3 bg-white border border-[rgba(15,23,42,0.08)]">
          <div className="text-[10.5px] font-mono uppercase" style={{ color: "#94A3B8", letterSpacing: "0.16em" }}>Totale</div>
          <div className="text-[18px] font-extrabold mt-1" style={{ color: "#0F172A", letterSpacing: "-0.02em" }}>€43,50</div>
        </div>
        <div className="rounded-lg p-3 bg-white border border-[rgba(15,23,42,0.08)]">
          <div className="text-[10.5px] font-mono uppercase" style={{ color: "#94A3B8", letterSpacing: "0.16em" }}>IVA</div>
          <div className="text-[14px] font-bold mt-1" style={{ color: "#0F172A" }}>22% · €7,84</div>
        </div>
        <div className="rounded-lg p-3 bg-white border border-[rgba(15,23,42,0.08)]">
          <div className="text-[10.5px] font-mono uppercase" style={{ color: "#94A3B8", letterSpacing: "0.16em" }}>Tipo</div>
          <div className="text-[14px] font-bold mt-1" style={{ color: "#0F172A" }}>Pranzo cliente</div>
        </div>
      </div>

      <div className="rounded-lg p-3 bg-white border border-[rgba(15,23,42,0.08)] flex items-center gap-3">
        <div className="w-12 h-12 rounded-md flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)", border: "1px solid rgba(15,23,42,0.08)" }}>
          <FileText className="w-5 h-5" style={{ color: "#64748B" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold truncate" style={{ color: "#0F172A" }}>scontrino_ristorante_dante.pdf</div>
          <div className="text-[11px]" style={{ color: "#64748B" }}>OCR italiano · 92% di confidenza</div>
        </div>
        <div className="inline-flex items-center gap-1 text-[10.5px] font-mono font-semibold px-2 py-1 rounded-full" style={{ background: "rgba(16,185,129,0.10)", color: "#047857" }}>
          <Check className="w-2.5 h-2.5" /> AUTO
        </div>
      </div>

      {done && (
        <div className="mt-4 rounded-xl px-4 py-3 reveal" style={{ background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.30)" }}>
          <div className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: "#047857" }}>
            <Check className="w-4 h-4" />
            Approvato · accodato all'export paghe di fine mese.
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Step 4: AI Report ---------- */
function ReportStep({ done, onAction }) {
  return (
    <div className="rounded-2xl p-5 md:p-6 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #0F172A 0%, #1E1B4B 100%)", minHeight: 320 }}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)" }}>
          <Sparkles className="w-4.5 h-4.5" />
        </div>
        <div>
          <div className="text-[14.5px] font-bold text-white">Report AI · Maggio 2026</div>
          <div className="text-[11.5px]" style={{ color: "rgba(255,255,255,0.6)" }}>Generato in 1.4 secondi · GPT-5</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="text-[10px] font-mono uppercase" style={{ color: "rgba(255,255,255,0.5)", letterSpacing: "0.16em" }}>Ore totali</div>
          <div className="text-[19px] font-extrabold mt-1 text-white">1.840h</div>
          <div className="inline-flex items-center gap-0.5 text-[10px] text-emerald-400 font-semibold mt-1">
            <TrendingUp className="w-2.5 h-2.5" /> +12%
          </div>
        </div>
        <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="text-[10px] font-mono uppercase" style={{ color: "rgba(255,255,255,0.5)", letterSpacing: "0.16em" }}>Presenza</div>
          <div className="text-[19px] font-extrabold mt-1 text-white">94,3%</div>
          <div className="text-[10px] mt-1" style={{ color: "rgba(255,255,255,0.5)" }}>su 22 giorni</div>
        </div>
        <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="text-[10px] font-mono uppercase" style={{ color: "rgba(255,255,255,0.5)", letterSpacing: "0.16em" }}>Anomalie</div>
          <div className="text-[19px] font-extrabold mt-1 text-amber-400">3</div>
          <div className="text-[10px] mt-1" style={{ color: "rgba(255,255,255,0.5)" }}>da rivedere</div>
        </div>
      </div>

      {/* Mini bar chart */}
      <div className="flex items-end gap-1.5 h-12 mb-4">
        {[40, 65, 45, 80, 70, 90, 60, 75, 85, 55, 95, 88].map((h, i) => (
          <div key={i} className="flex-1 rounded-t" style={{
            height: `${h}%`,
            background: i === 10 ? "linear-gradient(180deg, #C084FC 0%, #7C3AED 100%)" : "rgba(255,255,255,0.18)",
          }} />
        ))}
      </div>

      {/* AI insight */}
      <div className="rounded-xl p-3.5" style={{ background: "rgba(168,85,247,0.10)", border: "1px solid rgba(168,85,247,0.25)" }}>
        <div className="flex items-start gap-2.5">
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#C084FC" }} />
          <p className="text-[12.5px] leading-[1.5]" style={{ color: "rgba(255,255,255,0.92)" }}>
            <span className="font-semibold text-white">Insight AI:</span> Carico in aumento del 12% rispetto ad Aprile.
            Rilevate 3 timbrature anomale fuori dal raggio GPS. Suggerisco di approvare prima dell'export paghe.
          </p>
        </div>
      </div>

      {done && (
        <div className="mt-3 rounded-xl px-4 py-2.5 reveal" style={{ background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.30)" }}>
          <div className="flex items-center gap-2 text-[12.5px] font-semibold" style={{ color: "#34D399" }}>
            <Check className="w-3.5 h-3.5" />
            Report esportato · PDF inviato via email
          </div>
        </div>
      )}
    </div>
  );
}

const STEPS = [
  {
    title: "Timbra in 1 secondo",
    subtitle: "Inquadra il QR. Verixa fa il resto.",
    action: "Conferma timbratura",
    render: (p) => <QRStep {...p} />,
  },
  {
    title: "Richiedi ferie senza email",
    subtitle: "Due tap, il responsabile riceve la notifica.",
    action: "Invia richiesta",
    render: (p) => <VacationStep {...p} />,
  },
  {
    title: "Approva un rimborso in 11 secondi",
    subtitle: "OCR italiano + un click. Pronto per le paghe.",
    action: "Approva rimborso",
    render: (p) => <ExpenseStep {...p} />,
  },
  {
    title: "Report AI in 1.4 secondi",
    subtitle: "GPT-5 racconta il mese in italiano corretto.",
    action: "Esporta report",
    render: (p) => <ReportStep {...p} />,
  },
];

export function InteractiveTourModal({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const startTime = useRef(Date.now());

  useEffect(() => {
    if (open) {
      setStep(0);
      setDone(false);
      startTime.current = Date.now();
    }
  }, [open]);

  if (!open) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const handleAction = () => {
    if (!done) {
      setDone(true);
      return;
    }
    if (isLast) return;
    setStep((s) => s + 1);
    setDone(false);
  };

  const elapsed = Math.floor((Date.now() - startTime.current) / 1000);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
      style={{ background: "rgba(5,7,15,0.65)", backdropFilter: "blur(8px)" }}
      onClick={() => onOpenChange(false)}
      data-testid="tour-modal-backdrop"
    >
      <div
        className="relative w-full max-w-[640px] rounded-3xl overflow-hidden"
        style={{
          background: "#FFFFFF",
          boxShadow: "0 50px 100px -20px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)",
          maxHeight: "calc(100vh - 32px)",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="tour-modal"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 md:px-7 py-4 border-b" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-bold tracking-[0.18em] uppercase text-white"
                 style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)" }}>
              <Sparkles className="w-3 h-3" /> Tour live
            </div>
            <div className="inline-flex items-center gap-1 text-[11px] font-mono" style={{ color: "#64748B" }}>
              <Clock className="w-3 h-3" /> {elapsed}s / 60s
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="tour-modal-close"
            className="w-9 h-9 rounded-full inline-flex items-center justify-center transition-colors hover:bg-[rgba(15,23,42,0.06)]"
            aria-label="Chiudi tour"
          >
            <X className="w-4 h-4" style={{ color: "#0F172A" }} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 md:px-7 pt-6 pb-4 overflow-y-auto" style={{ flex: 1 }}>
          {/* Step title */}
          <div className="mb-5">
            <div className="text-[10.5px] tracking-[0.22em] uppercase font-semibold mb-2" style={{ color: "#3B82F6" }}>
              Passo {step + 1} di {STEPS.length}
            </div>
            <h3
              key={step}
              className="reveal"
              style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, color: "#0F172A", letterSpacing: "-0.03em", lineHeight: 1.1, fontSize: 26 }}
            >
              {current.title}
            </h3>
            <p className="mt-1.5 text-[14px]" style={{ color: "#475569" }}>
              {current.subtitle}
            </p>
          </div>

          {/* Mock visual */}
          <div key={`mock-${step}`} className="reveal">
            {current.render({ done, onAction: handleAction })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 md:px-7 py-4 border-t flex items-center justify-between gap-3" style={{ borderColor: "rgba(15,23,42,0.06)", background: "#FAFAF7" }}>
          <ProgressDots active={step} />

          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                type="button"
                onClick={() => { setStep((s) => Math.max(0, s - 1)); setDone(true); }}
                data-testid="tour-back-btn"
                className="w-10 h-10 rounded-full inline-flex items-center justify-center transition-colors hover:bg-[rgba(15,23,42,0.06)]"
                aria-label="Indietro"
                style={{ color: "#0F172A", border: "1px solid rgba(15,23,42,0.10)" }}
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}

            {isLast && done ? (
              <Link to="/register">
                <Button
                  data-testid="tour-cta-register"
                  className="rounded-full h-10 px-5 text-[13px] font-semibold border-0 text-white"
                  style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)", boxShadow: "0 10px 28px -8px rgba(124,58,237,0.5)" }}
                >
                  Inizia gratis 14 giorni <ArrowRight className="ml-2 w-3.5 h-3.5" />
                </Button>
              </Link>
            ) : (
              <Button
                onClick={handleAction}
                data-testid={`tour-action-${step}`}
                className="rounded-full h-10 px-5 text-[13px] font-semibold border-0 text-white"
                style={{ background: "linear-gradient(135deg, #3B82F6 0%, #7C3AED 100%)" }}
              >
                {!done ? current.action : (isLast ? "Continua" : "Prossimo passo")}
                <ArrowRight className="ml-2 w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default InteractiveTourModal;
